import { createSignal, type JSX, Show } from 'solid-js';
import { t } from '../i18n';
import { ApiError } from '../lib/api';
import type { OperatorRefusal } from '../lib/operator-credential';
import { promoteMessage, requestRunnerPromote } from '../lib/runner-promote';
import { Field } from './Field';
import { OperatorRefusalAlert } from './OperatorRefusalAlert';
import { Button } from './ui/Button';

// Promote a soaked canary runner build to stable (#6735) — the staff-only section of
// `<FleetRolloutModal/>`. The modal mounts it only for `is_platform_admin`; the write
// rides an `admin:releases` operator credential (`lib/runner-promote.ts`), never the
// session. A FREE-TEXT version, not a picker: the modal's version list reads the
// ledger, and the ledger holds no canary builds.
//
// The button is OUTLINE: the dialog's "Start the update" stays its one strong button.
// No animation, so the reduced-motion still frame is the section itself.

export function FleetPromoteSection(props: { readonly token: string | null }): JSX.Element {
  const [version, setVersion] = createSignal('');
  const [pending, setPending] = createSignal(false);
  const [said, setSaid] = createSignal<{ ok: boolean; text: string } | null>(null);
  const [refusal, setRefusal] = createSignal<OperatorRefusal | null>(null);

  const submit = (): void => {
    const v = version().trim();
    if (v === '' || pending()) return;
    setPending(true);
    setSaid(null);
    setRefusal(null);
    void requestRunnerPromote(v, props.token)
      .then((out) => {
        // The door refused: nothing was sent, and the alert names the remedy.
        if (out.kind !== 'ok') return setRefusal(out.kind);
        setSaid({
          ok: out.value.kind === 'accepted',
          text: promoteMessage(v, out.value, Date.now()),
        });
      })
      .catch((e: unknown) =>
        setSaid({
          ok: false,
          text: t('fleet.promote.failed', {
            reason: e instanceof ApiError ? String(e.status) : 'network',
          }),
        }),
      )
      .finally(() => setPending(false));
  };

  return (
    <section class="field-group" aria-labelledby="fleet-promote-title">
      <div class="separator" />
      <h3 id="fleet-promote-title" class="card__title">
        {t('fleet.promote.title')}
      </h3>
      <p class="card__desc">{t('fleet.promote.desc')}</p>
      <form
        class="field-group"
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
      >
        <Field
          label={t('fleet.promote.version')}
          id="promote-version"
          desc={t('fleet.promote.version_desc')}
        >
          {(a11y) => (
            <input
              {...a11y}
              class="input"
              type="text"
              autocomplete="off"
              spellcheck={false}
              value={version()}
              onInput={(e) => setVersion(e.currentTarget.value)}
            />
          )}
        </Field>
        <OperatorRefusalAlert refusal={refusal()} />
        <Show when={said()}>
          {(s) => (
            <Show
              when={s().ok}
              fallback={
                <p class="field__error" role="alert">
                  {s().text}
                </p>
              }
            >
              <div class="alert" role="status">
                <p class="alert__desc">{s().text}</p>
              </div>
            </Show>
          )}
        </Show>
        <div class="dialog__footer">
          <Button
            variant="outline"
            disabled={version().trim() === '' || pending()}
            onClick={submit}
          >
            {t(pending() ? 'fleet.promote.submitting' : 'fleet.promote.submit')}
          </Button>
        </div>
      </form>
    </section>
  );
}
